'use client';

/**
 * Hook for managing USDC transfer state and orchestrating the transfer flow.
 * Uses the tx-engine relay pipeline (same as buy/sell) so the relay pays gas fees.
 */

import {
  createTransactionTraceId,
  safeTxErrorCode,
  useTurnkeySigningDeps,
  useTurnkeySubmissionDeps,
} from '@morfi/backend-sdk-v2/client';
import { unitsToDecimal } from '@morfi/onramp-sdk';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { TOAST_IDS } from '@/config/toast-ids';
import { type DynamicTranslator, resolveApiErrorMessage } from '@/lib/errors/resolve-api-error';
import type { Network } from '@/lib/network';
import { getActiveNetwork } from '@/lib/network/active';
import { reportError, trackAction } from '@/lib/telemetry';
import {
  resolveConnectionConfig,
  useSubmitTransaction,
} from '@/lib/tx-engine/use-submit-transaction';
import { showToast } from '@/lib/ui/toast';
import { useInvalidateWalletBalances } from '@/queries/wallet-balance';
import type { WalletType } from '@/types/auth';

export type TransferStep = 'idle' | 'submitting' | 'confirmed' | 'failed';

export interface UseTransferUsdcReturn {
  step: TransferStep;
  error: string | null;
  signature: string | null;
  submit: (
    walletId: string,
    senderAddress: string,
    senderWalletType: WalletType,
    recipientAddress: string,
    amountD6: bigint,
    operationId?: string,
    network?: Network,
    beforeBroadcast?: () => Promise<void>,
  ) => Promise<string | null>;
  reset: () => void;
}

function getExplorerUrl(signature: string, network: Network): string {
  if (network === 'mainnet') {
    return `https://explorer.solana.com/tx/${signature}`;
  }
  return `https://explorer.solana.com/tx/${signature}?cluster=${network}`;
}

async function loadTransferBuilder(): Promise<
  typeof import('@/lib/solana/transfer').buildTransferUsdc
> {
  const { buildTransferUsdc } = await import('@/lib/solana/transfer');
  return buildTransferUsdc;
}

export function useTransferUsdc(): UseTransferUsdcReturn {
  const signingDeps = useTurnkeySigningDeps();
  const invalidateWalletBalances = useInvalidateWalletBalances();
  const turnkeyDeps = useTurnkeySubmissionDeps();
  const submitTx = useSubmitTransaction();
  const t = useTranslations('wallet.transfer');
  // Root translator for the shared API-error resolver (full dotted keys).
  // ALLOW-UNKNOWN: next-intl `t` is typed against the static message tree; the resolver dispatches dynamic SDK-emitted keys, so we narrow the function shape (not the value).
  const tRoot = useTranslations() as unknown as DynamicTranslator;

  const [step, setStep] = useState<TransferStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);

  async function submit(
    walletId: string,
    senderAddress: string,
    senderWalletType: WalletType,
    recipientAddress: string,
    amountD6: bigint,
    operationId?: string,
    network?: Network,
    beforeBroadcast?: () => Promise<void>,
  ): Promise<string | null> {
    const transferNetwork = network ?? getActiveNetwork();
    const traceId = createTransactionTraceId();
    const amountUsdc = unitsToDecimal(amountD6, 6);

    setStep('submitting');
    setError(null);
    setSignature(null);

    showToast({
      id: TOAST_IDS.TRANSFER_SUBMITTING,
      message: t('sending'),
      variant: 'loading',
      duration: Infinity,
    });

    trackAction('transfer_started', {
      traceId,
      amount: String(amountUsdc),
      walletType: senderWalletType,
    });

    try {
      const buildTransferUsdc = await loadTransferBuilder();

      const buildResult = await buildTransferUsdc({
        senderAddress,
        recipientAddress,
        amountD6,
        ...(operationId ? { operationId } : {}),
        network: transferNetwork,
        feePayerAddress: senderAddress,
      });

      const result = await submitTx(
        {
          instructions: buildResult.instructions,
          signers: buildResult.signers,
          computeUnits: buildResult.computeUnits,
        },
        {
          instructionName: 'transfer_usdc',
          connectionScope: { config: resolveConnectionConfig(transferNetwork) },
          traceId,
          signingDeps,
          activeWallet: {
            id: walletId,
            address: senderAddress,
            type: senderWalletType,
          },
          turnkeyDeps,
          ...(beforeBroadcast ? { beforeBroadcast } : {}),
        },
      );

      if (result.ok) {
        setStep('confirmed');
        setSignature(result.signature);

        showToast({
          id: TOAST_IDS.TRANSFER_SUBMITTING,
          message: t('toast_success'),
          variant: 'success',
          duration: 5000,
          action: {
            label: t('view_explorer'),
            onClick: (): void => {
              window.open(getExplorerUrl(result.signature, transferNetwork), '_blank');
            },
          },
        });

        trackAction('transfer_success', {
          traceId,
          amount: String(amountUsdc),
        });

        // Invalidate every wallet's USDC balance — broader than the sender
        // alone, by design: the transfer modal can act on any wallet in the
        // user's set and the recipient may be one of them. TanStack
        // prefix-matches `[WALLET_BALANCE_QUERY_KEY]` so the sender's
        // per-address entry is still included.
        invalidateWalletBalances();
        return result.signature;
      } else {
        const errorMessage = result.error.userMessageKey ?? 'Transfer failed';
        setStep('failed');
        setError(errorMessage);

        showToast({
          id: TOAST_IDS.TRANSFER_SUBMITTING,
          message: resolveApiErrorMessage(result.error, tRoot) ?? t('toast_failed'),
          variant: 'error',
        });

        trackAction('transfer_failed', {
          traceId,
          errorCode: safeTxErrorCode(result.error.code),
        });
      }
    } catch (err: unknown) {
      reportError(new Error('transfer_usdc_exception'), {
        component: 'useTransferUsdc',
        traceId,
        errorClass: err instanceof Error ? 'Error' : typeof err,
      });
      const errorMessage = err instanceof Error ? err.message : String(err);
      setStep('failed');
      setError(errorMessage);

      showToast({
        id: TOAST_IDS.TRANSFER_FAILED,
        message: resolveApiErrorMessage(err, tRoot) ?? t('toast_failed'),
        variant: 'error',
      });
    }
    return null;
  }

  function reset(): void {
    setStep('idle');
    setError(null);
    setSignature(null);
  }

  return { step, error, signature, submit, reset };
}
